import { z } from 'zod'

/**
 * **Die Ansichten von „Mein Tag"** ([Z-19](../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.12,
 * [E-374](../../../docs/ENTSCHEIDUNGEN.md#e-374)).
 *
 * Bis zum 30.09.2026 waren es zwei ([Z-12](../../../docs/phasen/zusatz-12-mein-tag-einfach.md)).
 * Nils: *„Einfach/Ausführlich/Kalender/Circle als schönen Ansichtknopf haben. Jeder User kann
 * sich selber seine «Standard» ansicht dann speichern."*
 *
 * | Modus | Die Frage, die er beantwortet |
 * |---|---|
 * | `einfach` | Was kommt, was läuft, was ist fertig? |
 * | `ausfuehrlich` | Dasselbe, aufgefächert nach Vorhaben, mit dem Feed |
 * | `kalender` | **Wann** mache ich es – passt es überhaupt in den Tag? |
 * | `circle` | **Woher** kommt es – und ist in dieser Welt noch Platz? |
 *
 * Die Reihenfolge ist die des Umschalters und steigt im Anspruch: die beiden Listen zuerst,
 * dann die beiden Bilder.
 *
 * **Warum hier in `shared` und nicht mehr nur im Web:** Seit es eine gespeicherte Standardansicht
 * am Konto gibt, muss der Server denselben Wertevorrat kennen – sonst nähme er eine Zeichenkette
 * an, die die Oberfläche nicht kennt, und der Nutzer landete stumm wieder auf „Einfach".
 */
export const TAGESMODI = ['einfach', 'ausfuehrlich', 'kalender', 'circle'] as const
export type Tagesmodus = (typeof TAGESMODI)[number]
export const tagesmodusSchema = z.enum(TAGESMODI)

/**
 * **Womit jemand anfängt, der nichts eingestellt hat.**
 *
 * `einfach` seit Z-12, und aus demselben Grund: Es ist die Antwort auf die Frage, die zu dem
 * Umschalter geführt hat (*„Das Mein Tag muss noch etwas übersichtlicher sein"*). Wer mehr will,
 * ist einen Klick entfernt.
 */
export const TAGESMODUS_VORGABE: Tagesmodus = 'einfach'

/**
 * **Was gilt: das Gerät oder das Konto?** (30.09.2026)
 *
 * Beides, und in dieser Reihenfolge:
 *
 * 1. **Was auf diesem Gerät zuletzt gewählt wurde.** Z-12 hat das bewusst so gebaut: Am großen
 *    Bildschirm will jemand vielleicht „Ausführlich", auf dem Telefon „Einfach". Eine reine
 *    Kontoeinstellung könnte das nicht unterscheiden.
 * 2. **Sonst die gespeicherte Standardansicht des Kontos** – das, was Nils wollte: auf einem
 *    neuen Gerät oder nach dem Leeren des Speichers landet man dort, wo man landen will.
 * 3. **Sonst `einfach`.**
 *
 * Das Konto überschreibt also nicht, was jemand hier gerade eingestellt hat – es fängt nur den
 * Fall ab, in dem das Gerät nichts weiß. Andersherum wäre der Umschalter auf dem Telefon nach
 * jedem Besuch am Schreibtisch wieder verstellt.
 */
export function tagesmodusWaehlen(
  vomGeraet: string | null | undefined,
  vomKonto: string | null | undefined,
): Tagesmodus {
  const gueltig = (wert: string | null | undefined): Tagesmodus | null =>
    wert && (TAGESMODI as readonly string[]).includes(wert) ? (wert as Tagesmodus) : null
  return gueltig(vomGeraet) ?? gueltig(vomKonto) ?? TAGESMODUS_VORGABE
}
